'use client';

import React, { HTMLAttributes, useEffect, useState, MouseEvent } from 'react';
import { avertaRegular } from '@/styles/fonts';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import parser from 'html-react-parser';
import { parseOptions } from '@/utils/parseOptions';

import { IDiscoverSicilyTab } from '@/dictionaries/pages/discoverSicily/types';

import { IDiscoverPugliaTab } from '@/dictionaries/pages/discoverPuglia/types';

import styles from './styles.module.css';

interface ISideNavigatorTabProps extends HTMLAttributes<HTMLDivElement> {
  tab: IDiscoverSicilyTab | IDiscoverPugliaTab;
  onSubTabClick?: (e: MouseEvent<HTMLDivElement>, page: string) => void;
  onTabClick?: () => void;
  tabId?: number;
  expandedTab?: number;
  expandedSubTab?: number;
  currentPage: string;
}

export const SideNavigatorTab: React.FC<ISideNavigatorTabProps> = ({
  tab,
  currentPage,
  tabId,
  onTabClick,
  expandedTab,
  expandedSubTab,
  onSubTabClick = () => {},
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (expandedTab) {
      setIsExpanded(expandedTab === tabId);
    }
  }, [expandedTab, tabId]);

  return (
    <div
      className={cn(
        'pt-[30px] px-[40px] pb-[10px] border-b border-gray-200 text-sm',
        avertaRegular.className,
        className,
      )}>
      {tab.href ? (
        <Link href={tab.href}>
          <h6 className="font-semibold text-gray-900 h-10 text-subtitle">
            {tab.title}
          </h6>
        </Link>
      ) : (
        <>
          <button
            className="h-min"
            onClick={() =>
              onTabClick
                ? onTabClick()
                : setIsExpanded((prevState) => !prevState)
            }>
            <div
              className={`
            flex flex-row items-center w-full pr-11 before:content-['+'] before:h-10 before:w-6 text-subtitle before:text-center
            before:text-secondary mr-1 overflow-hidden 
            ${isExpanded && "before:content-['-'] mb-4"}`}>
              <span className="font-semibold text-gray-900 text-subtitle text-start leading-[1]">
                {tab.title}
              </span>
            </div>
          </button>
          {tab.subtabs &&
            tab.subtabs.map((subTab, index) => (
              <div
                key={index}
                onClick={(e) => onSubTabClick(e, subTab.page)}
                className={`h-0 flex transition-all ease-out duration-200 flex-col mb-0 overflow-hidden ${
                  isExpanded && 'h-fit mb-2.5'
                }`}>
                <Link
                  shallow={true}
                  scroll={false}
                  className={cn(
                    `${
                      styles['link']
                    } transition delay-200 flex flex-col w-fit space-y-4 ml-8 h-fit hover:text-black ${
                      subTab.page === currentPage &&
                      '!text-black ' + styles['selected-link']
                    }`,
                    { 'text-gray-700': !!subTab.description },
                  )}
                  href={subTab.href}>
                  <span className="text-base">{subTab.title}</span>
                </Link>
                {isExpanded && expandedSubTab === index && (
                  <div className="h-auto desktop:hidden ml-8 pl-2 pt-2 text-base leading-[1.6] !mb-[6px] !mt-0">
                    {parser(subTab.description || '', parseOptions)}
                  </div>
                )}
              </div>
            ))}
        </>
      )}
    </div>
  );
};
